// lib/authClient.ts
import { withFrontendOrigin } from "@/lib/apiHeaders";
import { BROWSER_API_PREFIX, getBackendUrl } from "@/lib/backendUrl";

export type Role = "user" | "admin";

export type TeacherRole = "staff";

export type EffectiveRole = Role | TeacherRole;

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: EffectiveRole;
  section?: string | null;
  newsletterSubscribed?: boolean;
}

const AUTH_SESSION_KEY = "auth_session";
const LEGACY_TOKEN_KEY = "jwt_token";

function buildUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (typeof window !== "undefined") {
    return `${BROWSER_API_PREFIX}${normalized}`;
  }
  return `${getBackendUrl()}${normalized}`;
}

function hasApiBase(): boolean {
  if (typeof window !== "undefined") return true;
  return Boolean(getBackendUrl());
}

/** Indica che l'utente ha effettuato login in questa tab (non contiene il JWT). */
export function markAuthSessionActive() {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(AUTH_SESSION_KEY, "1");
  localStorage.setItem(AUTH_SESSION_KEY, String(Date.now()));
}

export function isAuthSessionMarked(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(AUTH_SESSION_KEY) === "1";
}

/** True se tab o localStorage indicano una sessione possibile (evita GET /session inutili). */
export function mightHaveAuthSession(): boolean {
  if (typeof window === "undefined") return false;
  if (isAuthSessionMarked()) return true;
  return localStorage.getItem(AUTH_SESSION_KEY) != null;
}

/** Rimuove flag sessione e token legacy; opzionale broadcast logout tra tab. */
export function clearAuthSession(broadcast = false) {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(AUTH_SESSION_KEY);
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem("login_time");
  localStorage.removeItem(AUTH_SESSION_KEY);
  if (broadcast) {
    localStorage.setItem("logout", String(Date.now()));
  }
  window.dispatchEvent(new Event("auth:changed"));
}

/** Migrazione: elimina JWT salvato in sessionStorage (non più usato). */
export function purgeLegacyAuthToken() {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(LEGACY_TOKEN_KEY);
  sessionStorage.removeItem("login_time");
}

/**
 * Fetch autenticato via cookie HttpOnly (`credentials: include`).
 * Il JWT non è più leggibile dal JavaScript.
 */
export async function authFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  if (!hasApiBase()) {
    throw new Error("NEXT_PUBLIC_BACKEND_URL non configurato");
  }

  const isFormData =
    typeof FormData !== "undefined" && init?.body instanceof FormData;

  const baseHeaders = Object.fromEntries(
    new Headers(init?.headers).entries(),
  );
  // Non forzare Content-Type su FormData: il browser deve impostare il boundary.
  if (isFormData) {
    delete baseHeaders["Content-Type"];
    delete baseHeaders["content-type"];
  }

  const headers = withFrontendOrigin(baseHeaders);

  return fetch(buildUrl(path), {
    ...init,
    credentials: "include",
    headers,
  });
}

/** Parse JSON da risposte del proxy /api/backend. */
export async function parseAuthJson<T = Record<string, unknown>>(
  res: Response,
): Promise<T | null> {
  try {
    return (await res.json()) as T;
  } catch {
    try {
      const text = await res.text();
      if (!text.trim()) return null;
      return JSON.parse(text) as T;
    } catch {
      return null;
    }
  }
}

/**
 * @deprecated Il token non è più esposto al frontend. Usa `authFetch` o `fetchSessionUser`.
 */
export function getAuthToken(): null {
  return null;
}

/** @deprecated Usa `markAuthSessionActive` dopo login e `clearAuthSession` al logout. */
export function setAuthToken() {
  markAuthSessionActive();
}

/** @deprecated Usa `clearAuthSession`. */
export function clearAuthToken(broadcast = false) {
  clearAuthSession(broadcast);
}

export async function fetchSessionUser(options?: {
  signal?: AbortSignal;
  autoLogout?: boolean;
  /** Se true, su 401/403 rimuove flag sessione obsoleti in storage. */
  clearStale?: boolean;
  timeoutMs?: number;
}): Promise<SessionUser | null> {
  const {
    signal,
    autoLogout = false,
    clearStale = false,
    timeoutMs = 8000,
  } = options || {};

  if (!hasApiBase()) return null;

  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);

  try {
    const res = await authFetch("/auth/session", {
      method: "GET",
      cache: "no-store",
      signal: signal ?? ctrl.signal,
    });

    if (res.status === 401 || res.status === 403) {
      if (autoLogout || clearStale) clearAuthSession(autoLogout);
      return null;
    }
    if (!res.ok) return null;

    let data: { user?: SessionUser } | null = null;
    try {
      data = await res.json();
    } catch {
      return null;
    }
    return data?.user ?? null;
  } catch {
    return null;
  } finally {
    clearTimeout(id);
  }
}

export async function apiFetch(
  path: string,
  init?: RequestInit & { autoLogout?: boolean; redirectOnLogout?: string },
): Promise<Response> {
  const { autoLogout = false, redirectOnLogout, ...rest } = init || {};

  const res = await authFetch(path, rest);

  if (res.status === 401 || res.status === 403) {
    if (autoLogout) {
      clearAuthSession(true);
      if (typeof window !== "undefined" && redirectOnLogout) {
        window.location.href = redirectOnLogout;
      }
    }
  }
  return res;
}

if (typeof window !== "undefined") {
  purgeLegacyAuthToken();
}
